/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { db } from '../../config/firebaseConfig';
import { transitionToStaking } from '../../utils/astrapos/stageTransitions';

/**
 * WAITING stage: Players join and press Ready button
 * Transition to STAKING when minimum ready players reached
 */
const WaitingStage = ({ roomId, roomData, userData, role }) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [participantDetails, setParticipantDetails] = useState({});

  useEffect(() => {
    const fetchParticipantDetails = async () => {
      const allPks = [...(roomData.participants || []), ...(roomData.miners || [])];
      if (allPks.length === 0) return;

      try {
        const usersRef = db.collection('users');
        const querySnapshot = await usersRef.where('publicKey', 'in', allPks).get();
        const details = {};
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          details[userData.publicKey] = userData;
        });
        setParticipantDetails(details);
      } catch (error) {
        console.error("Error fetching participant details:", error);
      }
    };

    if (roomData.participants || roomData.miners) {
      fetchParticipantDetails();
    }
  }, [roomData.participants, roomData.miners]);

  const minPlayers = roomData.astraposConfig?.minPlayers || 3;
  const readyCount = roomData.readyPlayers?.length || 0;
  const allPlayers = (roomData.participants || []).concat(
    roomData.miners || []
  );

  // Check if user is already ready
  useEffect(() => {
    const userIsReady = roomData.readyPlayers?.includes(userData.publicKey);
    setIsReady(!!userIsReady);
  }, [roomData.readyPlayers, userData.publicKey]);

  // Auto-start staking when minimum players ready
  const handleAutoStartStaking = useCallback(async () => {
    try {
      // Try to transition - only one client will succeed
      await transitionToStaking(roomId, minPlayers);
    } catch (error) {
      // Silently fail - another client already triggered it
      console.log('Staking already started by another client');
    }
  }, [roomId, minPlayers]);

  // Check if enough players are ready to start staking
  useEffect(() => {
    if (readyCount >= minPlayers && !isLoading) {
      // Auto-trigger staking transition if first client to detect
      handleAutoStartStaking();
    }
  }, [readyCount, minPlayers, isLoading, handleAutoStartStaking]);

  const handleReady = async () => {
    if (userData.balance <= 0) {
      toast.error(
        'Not Eligible For Staking Game. Balance must be greater than 0.'
      );
      return;
    }

    if (isReady) {
      toast.info('You are already marked as ready');
      return;
    }

    setIsLoading(true);
    try {
      const roomRef = doc(db, 'rooms', roomId);
      await updateDoc(roomRef, {
        readyPlayers: arrayUnion(userData.publicKey),
      });
      setIsReady(true);
      toast.success('You are ready! Waiting for other players...');
    } catch (error) {
      console.error('Error marking as ready:', error);
      toast.error('Failed to mark as ready');
    } finally {
      setIsLoading(false);
    }
  };

  const playerList = allPlayers.map((pk) => {
    const participant = participantDetails[pk];
    return {
        publicKey: pk,
        isReady: roomData.readyPlayers?.includes(pk),
        name: participant ? participant.name : null,
        email: participant ? participant.email : null,
    }
  });

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 10)}...${address.substring(
      address.length - 8
    )}`;
  };

  const progressPercentage = Math.min((readyCount / minPlayers) * 100, 100);

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-8">
      {/* Header */}
      <div className="text-center relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/10 blur-[100px] rounded-full -z-10"></div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">
          Waiting Room
        </h1>
        <p className="text-white/40 text-lg">
          Get ready to participate in the staking game
        </p>
      </div>

      {/* Main Status Card */}
      <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl p-8 border border-white/5 relative overflow-hidden group">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent opacity-50"></div>

        <div className="text-center mb-8 relative z-10">
          <div className="inline-block p-4 rounded-full bg-white/[0.02] border border-white/5 mb-4 shadow-lg shadow-black/20">
            <div className="relative w-24 h-24 flex items-center justify-center">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-white/5"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                />
                <path
                  className="text-purple-500"
                  strokeDasharray={`${progressPercentage}, 100`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-white">
                  {readyCount}
                </span>
                <span className="text-[10px] uppercase text-white/40 font-bold tracking-wider">
                  of {minPlayers}
                </span>
              </div>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-white mb-1">
            Players Ready
          </h2>
          <p className="text-white/40 text-sm">
            Waiting for{' '}
            <span className="text-white font-medium">
              {Math.max(0, minPlayers - readyCount)}
            </span>{' '}
            more players
          </p>
        </div>

        {/* User Status Row */}
        <div className="mb-8 p-4 bg-black/20 rounded-xl border border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto overflow-hidden">
            <img
              src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.email}`}
              alt="avatar"
              className="w-12 h-12 rounded-full bg-white/5 ring-2 ring-white/5 shrink-0"
            />
            <div className="min-w-0">
              <p className="text-white font-medium truncate">
                {userData.email}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <p className="text-emerald-500/80 text-xs font-mono uppercase tracking-wide">
                  Connected
                </p>
              </div>
            </div>
          </div>

          {userData.balance <= 0 ? (
            <div className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium flex items-center gap-2 animate-pulse">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              Insufficient Balance
            </div>
          ) : (
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm border transition-all ${
                isReady
                  ? 'bg-green-500/10 text-green-400 border-green-500/20 bg-[url("https://www.transparenttextures.com/patterns/cubes.png")]'
                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
              }`}
            >
              {isReady ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Ready to Play
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                  Not Ready
                </>
              )}
            </div>
          )}
        </div>

        {/* Action Button */}
        {!isReady && (
          <button
            onClick={handleReady}
            disabled={isLoading || userData.balance <= 0}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all shadow-lg active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed ${
              userData.balance <= 0
                ? 'bg-white/5 text-white/20 border border-white/5'
                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-purple-500/20 border border-t-white/10'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Marking Ready...
              </span>
            ) : (
              "I'm Ready to Start"
            )}
          </button>
        )}

        {isReady && (
          <div className="w-full py-4 bg-green-500/10 border border-green-500/20 rounded-xl text-center">
            <p className="text-green-400 font-medium flex items-center justify-center gap-2 animate-pulse">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              Waiting for other players...
            </p>
          </div>
        )}
      </div>

      {/* Participants List */}
      <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden flex flex-col">
        <div className="p-6 border-b border-white/5 bg-white/[0.02]">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/10">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            Participants
            <span className="ml-2 px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-mono">
              {playerList.length}
            </span>
          </h3>
        </div>

        <div className="p-4 space-y-2">
          {playerList.length > 0 ? (
            playerList.map((player, idx) => (
              <div
                key={idx}
                className="group flex items-center justify-between p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-transparent hover:border-white/5 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white/40 bg-white/5 border border-white/5 ${player.isReady ? 'ring-2 ring-green-500/30' : 'ring-1 ring-white/10'}`}
                    >
                      {(idx + 1).toString().padStart(2, '0')}
                    </div>
                    {player.isReady && (
                      <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border-2 border-[#13151b]">
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          className="text-[#13151b]"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-white/80 font-mono text-sm tracking-wide">
                      {player.name || player.email || formatAddress(player.publicKey)}
                    </p>
                  </div>
                </div>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    player.isReady
                      ? 'bg-green-500/10 text-green-400 border-green-500/10'
                      : 'bg-white/5 text-white/40 border-white/5'
                  }`}
                >
                  {player.isReady ? 'Ready' : 'Waiting...'}
                </span>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-white/30 text-sm">
                No other participants joined yet.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Info Card - Bottom */}
      <div className="bg-gradient-to-br from-indigo-500/10 to-blue-500/10 backdrop-blur-xl rounded-2xl p-6 border border-indigo-500/10">
        <div className="flex gap-4">
          <div className="mt-1">
            <div className="p-2 bg-indigo-500/20 rounded-lg text-indigo-300">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </div>
          </div>
          <div>
            <h4 className="text-indigo-200 font-semibold mb-1">How it works</h4>
            <p className="text-indigo-200/60 text-sm leading-relaxed">
              Once{' '}
              <strong className="text-indigo-200">{minPlayers} players</strong>{' '}
              are ready, the staking game will begin automatically. Each player
              will have{' '}
              <strong className="text-indigo-200">
                {roomData.astraposConfig?.stakingDuration || 2} minutes
              </strong>{' '}
              to stake their coins. Be prepared to act fast!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default WaitingStage;
